import {Spec} from 'immutability-helper';
import $ from 'jquery';
import React from 'react';
import ReactDOMServer from 'react-dom/server';
import _ from 'underscore';

import {Permissions} from 'auth/PermissionConstants';
import * as benchlingUtil from 'BenchlingUtil';
import {EventConstants} from 'constants/EventConstants';
import {TagSchema} from 'constants/RegistryConstants';
import {User} from 'constants/UserConstants';
import {SchemaTag} from 'folder-items/FileConstants';
import AppDispatcher from 'lib/Dispatcher';
import * as DataTableActions from 'notebook/data-tables/DataTableActions';
import {StructuredData} from 'notebook/data-tables/DataTableConstants';
import {
  EntryProtocol,
  EntryVersion,
  FromServerId,
  ServerEntryVersion,
  ToServerId,
} from 'notebook/ELNConstants';
import {CidIdMap} from 'notebook/notes/NoteConstants';
import {Review} from 'notebook/reviews/ReviewConstants';
import * as TransferTableActions from 'notebook/transfer-tables/TransferTableActions';
// @ts-expect-error
import EntryVersionUtil from 'notebook/util/EntryVersionUtil';
import {EntryNote} from 'notebook/util/NoteUtilConstants';
import notifications, {Response} from 'notifications';
import * as popup from 'Popup';
import * as ModelActions from 'redux-stores/ModelActions';
import {getStore} from 'redux-stores/Store';
import * as LoadingScreen from 'ui-library/LoadingScreen';
import pick from 'util/pick';
import * as StageRunActions from 'workflows/StageRunActions';
import {WorkflowActionType} from 'workflows/WorkflowActions';
import {
  InputTableConfig,
  ServerStageEntry,
  StageEntryDetails,
  StageEntryDocument,
  StageEntrySummaryData,
  StageRun,
} from 'workflows/WorkflowConstants';

import * as StageEntryDAO from './StageEntryDAO';
import StageEntryStore from './StageEntryStore';
import * as StageEntryUtil from './StageEntryUtil';

export enum StageEntryActionType {
  WORKFLOW_STAGE_ENTRIES_FETCH_COMPLETED = 'WORKFLOW_STAGE_ENTRIES_FETCH_COMPLETED',
  WORKFLOW_STAGE_ENTRY_SAVE_COMPLETED = 'WORKFLOW_STAGE_ENTRY_SAVE_COMPLETED',
}

/**
 * Applies `op` to the stage entry with id `stageEntryId`, and merges in the specified `cidIdMap`.
 * If `op` introduces new models, it should specify the ids in `cidIdMap`.
 */
export function update(
  stageEntryId: string,
  op: Spec<StageEntryDocument>,
  {bumpVersion = true, cidIdMap = {}}: {bumpVersion?: boolean; cidIdMap?: CidIdMap} = {}
): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.WORKFLOW_STAGE_ENTRY_UPDATE,
    stageEntryId,
    op,
    cidIdMap,
    bumpVersion,
  });
}

export function postServerSideUpdate(stageEntryId: string): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.WORKFLOW_STAGE_ENTRY_SERVER_SIDE_UPDATE,
    stageEntryId,
    bumpVersion: false,
  });
}

export function revert(
  stageEntryId: string,
  op: Spec<StageEntryDocument>,
  restoreData: SelectionRestoreData
): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.WORKFLOW_STAGE_ENTRY_REVERT,
    stageEntryId,
    op,
    restoreData,
  });
}

export function updateProtocol(
  stageEntryId: string,
  stageEntryDocument: StageEntryDocument,
  protocolCid: string,
  op: Spec<EntryProtocol>
): void {
  update(
    stageEntryId,
    StageEntryUtil.entryVersionUpdate({
      $apply: (entryVersion) => EntryVersionUtil.withEntryProtocolUpdated(entryVersion, protocolCid, op),
    })
  );
}

export function addProtocol(
  stageEntryId: string,
  stageEntryDocument: StageEntryDocument,
  protocol: EntryProtocol
): void {
  const cidIdMap = {};
  update(
    stageEntryId,
    StageEntryUtil.entryVersionUpdate({
      $apply: (entryVersion) => EntryVersionUtil.withProtocolAdded(entryVersion, protocol, cidIdMap),
    })
  );
}

export function removeProtocol(
  stageEntryId: string,
  stageEntryDocument: StageEntryDocument,
  epCid: string
): void {
  update(
    stageEntryId,
    StageEntryUtil.entryVersionUpdate({
      $apply: (entryVersion) => EntryVersionUtil.withEntryProtocolRemoved(entryVersion, epCid),
    })
  );
}

export function revertProtocol(
  stageEntryId: string,
  stageEntryDocument: StageEntryDocument,
  revertedEntryProtocol: EntryProtocol,
  restoreData: SelectionRestoreData
): void {
  revert(
    stageEntryId,
    {
      $apply: (entryVersion) =>
        EntryVersionUtil.withEntryProtocolReverted(entryVersion, revertedEntryProtocol),
    },
    restoreData
  );
}

export function reorderProtocols(
  stageEntryId: string,
  stageEntryDocument: StageEntryDocument,
  cids: Array<string>
): void {
  update(
    stageEntryId,
    StageEntryUtil.entryVersionUpdate({
      $apply: (entryVersion) => EntryVersionUtil.withEntryProtocolsReordered(entryVersion, cids),
    })
  );
}

export function registerObjects(stageEntryId: string, cidIdMap: CidIdMap): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.WORKFLOW_STAGE_ENTRY_REGISTER_OBJECTS,
    stageEntryId,
    cidIdMap,
  });
}

export function updateNote(stageEntryId: string, note: EntryNote): void {
  update(stageEntryId, StageEntryUtil.entryVersionUpdate({$merge: {note}}));
}

export function setName(stageEntryId: string, name: string): void {
  update(stageEntryId, {$merge: {name}});
}

interface StageEntrySaveCompletedAction {
  type: StageEntryActionType.WORKFLOW_STAGE_ENTRY_SAVE_COMPLETED;
  stageEntryId: string;
  savedVersion: number;
  versionId: number;
  cidIdMap: CidIdMap;
  structuredData: StructuredData;
  summaryData: StageEntrySummaryData;
}

/**
 * Save the open stage entry via a throttled request.
 */
export function save(stageEntryId: string): JQueryPromise<boolean> {
  /**
   * Provide a snapshot of the relevant entry state found in the store for performing a save.
   * This should be called on both the leading and trailing edge of the throttled save so that we can
   * save the latest version of the entry if it is still in the store, or perform a best-effort save if
   * the entry was removed from the store before the trailing edge was hit.
   *
   * @return {Object} a snapshot of the entry store state:
   *   - openVersion: what to set as the savedVersion on completed save
   *   - serverStageEntry: a server-friendly representation for patching the entry
   *   - clientStageEntry:
   *       The client representation of the entry (which corresponds to the serverStageEntry).
   *       This is useful for the client to perform operations on the persisted entry post-save.
   */

  function getStoreSnapshotForSave(): {
    openVersion: number;
    hasUnsavedChanges: boolean;
    serverStageEntry: {
      name: string;
      entryVersion: ServerEntryVersion<ToServerId>;
      metadata: {
        latestVersionId: number;
        clientId: string;
        action: string | null;
      };
    };
    clientStageEntry: StageEntryDocument;
    isLatest: boolean;
  } {
    const {stageEntryDocument, openVersion, metadata, isLatest} =
      StageEntryStore.getEditorState(stageEntryId);
    const preparedStageEntryDocument = StageEntryStore.prepareForSave(stageEntryId, stageEntryDocument);
    const serverStageEntry = {
      ...preparedStageEntryDocument,
      metadata,
    };
    const hasUnsavedChanges = StageEntryStore.hasUnsavedChanges(stageEntryId);
    return {
      openVersion,
      hasUnsavedChanges,
      serverStageEntry,
      clientStageEntry: stageEntryDocument,
      isLatest,
    };
  }
  const dataAtSaveTime = getStoreSnapshotForSave();
  // Create a deferred promise that will be resolved once the save has completed.
  const deferredPromise = $.Deferred<boolean>();
  benchlingUtil.asyncThrottle(`template-save-${stageEntryId}`, () => {
    let dataToUse;
    if (StageEntryStore.inStore(stageEntryId)) {
      dataToUse = getStoreSnapshotForSave();
    } else {
      dataToUse = dataAtSaveTime;
    }
    const {openVersion, hasUnsavedChanges, serverStageEntry, clientStageEntry, isLatest} = dataToUse;
    if (!hasUnsavedChanges || !isLatest) {
      return deferredPromise.resolve(false).promise();
    }

    AppDispatcher.dispatch({
      actionType: EventConstants.WORKFLOW_STAGE_ENTRY_SAVE,
      stageEntryId,
    });
    const xhr = StageEntryDAO.update(stageEntryId, serverStageEntry)
      .done(
        ({
          cidIdMap,
          structuredData,
          summaryData,
        }: {
          cidIdMap: CidIdMap;
          structuredData: StructuredData;
          summaryData: StageEntrySummaryData;
        }) => {
          getStore().dispatch({
            type: StageEntryActionType.WORKFLOW_STAGE_ENTRY_SAVE_COMPLETED,
            stageEntryId,
            savedVersion: openVersion,
            versionId: cidIdMap[serverStageEntry.entryVersion.cid] as number,
            cidIdMap,
            structuredData,
            summaryData,
          });
          // Necessary to dispatch until StageEntryStore is also in Redux
          AppDispatcher.dispatch({
            actionType: EventConstants.WORKFLOW_STAGE_ENTRY_SAVE_COMPLETED,
            stageEntryId,
            savedVersion: openVersion,
            versionId: cidIdMap[serverStageEntry.entryVersion.cid],
            cidIdMap,
            structuredData,
            summaryData,
          });
          DataTableActions.fetchDataTablesValidationStatus(stageEntryId, clientStageEntry.entryVersion.note);
          // Resolve the deferred promise to signal that the save has completed.
          deferredPromise.resolve(true);
        }
      )
      .fail((response: Response) => {
        const friendlyMessage = notifications.getFriendlyError(response);
        if (friendlyMessage != null) {
          notifications.error(friendlyMessage, {
            id: 'template-save-fail',
            duration: 0,
          });
        }
        AppDispatcher.dispatch({
          actionType: EventConstants.WORKFLOW_STAGE_ENTRY_SAVE_FAILED,
          stageEntryId,
          error: response,
        });
        // Resolve the deferred promise to signal that the save has failed.
        deferredPromise.resolve(false);
      });
    return xhr;
  });
  // Return the deferred promise so that the caller can wait for the save to complete.
  return deferredPromise.promise();
}

export function dispatchStageEntryDetailsChange(stageEntryDetails: StageEntryDetails): void {
  const {
    stageEntry,
    stageRuns,
    stageFlowNodes,
    registrationRecordListsById,
    transferOperationListsById,
    transferOperationsById,
    inputSamplesOrder,
    outputSamplesOrder,
    workflowStageOrderedStageRunIds,
  } = stageEntryDetails;

  StageRunActions.dispatchStageRunsFetchedAndFetchMissingSamples({
    stageRuns: Object.values(stageRuns),
  });
  getStore().dispatch({
    type: WorkflowActionType.WORKFLOW_STAGE_ENTRY_DETAILS_FETCH_COMPLETED,
    stageEntry,
    stageFlowNodes,
    stageRuns,
    inputSamplesOrder,
    outputSamplesOrder,
    workflowStageOrderedStageRunIds,
  });
  if (registrationRecordListsById) {
    getStore().dispatch(ModelActions.registrationRecordListsFetchCompleted(registrationRecordListsById));
    StageRunActions.fetchMissingRegisteredSamples(Object.values(registrationRecordListsById));
  }
  getStore().dispatch(
    TransferTableActions.transferOperationListsFetchCompleted({
      transferOperationListsById,
      transferOperationsById,
    })
  );
  StageRunActions.fetchMissingTransferredSamples(Object.values(transferOperationsById));
}

export function dispatchStageEntryVersionChange(
  entryVersion: ServerEntryVersion<FromServerId>,
  stageEntry: ServerStageEntry
): void {
  const stageEntryDocument = StageEntryUtil.getEntryDocument(stageEntry, entryVersion);
  AppDispatcher.dispatch({
    actionType: EventConstants.WORKFLOW_STAGE_ENTRY_DOCUMENT_FETCH_COMPLETED,
    stageEntryId: stageEntry.id,
    stageEntryDocument,
  });
  const {stageEntryDocument: clientStageEntry} = StageEntryStore.getEditorState(stageEntry.id);
  DataTableActions.fetchDataTablesValidationStatus(stageEntry.id, clientStageEntry.entryVersion.note);
}

export function dispatchStageEntryFetchStarted(stageEntryId: string): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.WORKFLOW_STAGE_ENTRY_DOCUMENT_FETCH_STARTED,
    stageEntryId,
  });
}

export function loadVersion(stageEntryId: string, versionId: number): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.WORKFLOW_STAGE_ENTRY_LOAD_VERSION,
    stageEntryId,
    versionId,
  });
  StageEntryDAO.getVersion(stageEntryId, versionId)
    .done((response: EntryVersion) => {
      const {stageEntryDocument, metadata} = StageEntryStore.getEditorState(stageEntryId);
      AppDispatcher.dispatch({
        actionType: EventConstants.WORKFLOW_STAGE_ENTRY_LOAD_VERSION_COMPLETED,
        stageEntryId,
        versionId,
        version: StageEntryUtil.replaceEntryVersion(
          // Here we have to merge in the `latestVersionId` value from metadata. Without it, the value
          // gets out of sync with the truth, simply because stage entries don't return the actual
          // value in their `to_dict` (normal entries do).
          //
          // See VersionedItemSubstore.getEditorState, which is where the value is added to metadata,
          // and StageEntryUtil.getEntryDocument, where it's read from the entry version directly.
          //
          {...stageEntryDocument, latestVersionId: metadata.latestVersionId},
          response
        ),
      });
    })
    .fail((response) => {
      AppDispatcher.dispatch({
        actionType: EventConstants.WORKFLOW_STAGE_ENTRY_LOAD_VERSION_FAILED,
        stageEntryId,
        versionId,
      });
    });
}

export function fetchVersions(stageEntryId: string): void {
  if (!StageEntryStore.inStore(stageEntryId)) {
    return;
  }
  // Look up the currently open version to ensure that it is not coarsened but always returned in the
  // results of the fetch.
  const {openVersionId} = StageEntryStore.getEditorState(stageEntryId);

  AppDispatcher.dispatch({
    actionType: EventConstants.WORKFLOW_STAGE_ENTRY_FETCH_VERSIONS,
    stageEntryId,
  });
  StageEntryDAO.getVersions(stageEntryId, {openVersionId})
    .done((response: {data: Array<EntryVersion>; parent: unknown}) => {
      AppDispatcher.dispatch(
        // eslint-disable-next-line no-restricted-syntax
        _.extend(
          {
            actionType: EventConstants.WORKFLOW_STAGE_ENTRY_FETCH_VERSIONS_COMPLETED,
            stageEntryId,
            versions: response.data,
          },
          pick(response, 'parent')
        )
      ); // parent key present with null value: the parent was deleted
      // omitted parent key: there is no parent.
    })
    .fail(() => {
      AppDispatcher.dispatch({
        actionType: EventConstants.WORKFLOW_STAGE_ENTRY_FETCH_VERSIONS_FAILED,
        stageEntryId,
      });
    });
}

export function fetchEntryDetails(
  stageEntryId: string,
  {deferred}: {deferred?: JQueryDeferred<void>} = {}
): JQueryPromise<{
  stageEntryDetails: StageEntryDetails;
  entryVersion: ServerEntryVersion<FromServerId>;
}> {
  dispatchStageEntryFetchStarted(stageEntryId);
  return StageEntryDAO.getWithEntryVersion(stageEntryId)
    .done(
      ({
        stageEntryDetails,
        entryVersion,
      }: {
        stageEntryDetails: StageEntryDetails;
        entryVersion: ServerEntryVersion<FromServerId>;
      }) => {
        dispatchStageEntryDetailsChange(stageEntryDetails);
        dispatchStageEntryVersionChange(entryVersion, stageEntryDetails.stageEntry);
        if (deferred != null) {
          deferred.resolve();
        }
      }
    )
    .fail((response) => {
      AppDispatcher.dispatch({
        actionType: EventConstants.WORKFLOW_STAGE_ENTRY_DOCUMENT_FETCH_FAILED,
        stageEntryId,
        error: response,
      });
      if (deferred != null) {
        deferred.reject(response);
      }
    });
}

export function close(stageEntryId: string): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.WORKFLOW_STAGE_ENTRY_CLOSE,
    stageEntryId,
  });
}

export function invalidate(stageEntryId: string): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.WORKFLOW_STAGE_ENTRY_INVALIDATE,
    stageEntryId,
  });
}

export function revalidate(stageEntryId: string): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.WORKFLOW_STAGE_ENTRY_DOCUMENT_FETCH_FAILED,
    stageEntryId,
    // Since revalidate is called when an ActionFunction is aborted, the stage entry will not be out of date,
    // unlike when we dispatch the same event when failing to reload or open the entry.
    clearDirtyState: true,
  });
}

export function refresh(stageEntryIds: Array<string> | Set<string>): void {
  for (const stageEntryId of stageEntryIds) {
    if (StageEntryStore.inStore(stageEntryId)) {
      AppDispatcher.dispatch({
        actionType: EventConstants.WORKFLOW_STAGE_ENTRY_INVALIDATE,
        stageEntryId,
      });
      // This may be optimized by creating a single bulk request.
      fetchEntryDetails(stageEntryId);
    }
  }
}

export function updateReview(stageEntryId: string, review: Review, permissions: Permissions): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.WORKFLOW_STAGE_ENTRY_UPDATE,
    stageEntryId,
    op: {$merge: {review, permissions}},
    bumpVersion: false,
  });
}

export function setTagSchema(stageEntryId: string, tagSchema: TagSchema): void {
  update(stageEntryId, {$merge: {tagSchema}}, {bumpVersion: false});
}

export function setAuthors(stageEntryId: string, authors: Array<User>): void {
  update(stageEntryId, {$merge: {authors}}, {bumpVersion: false});
}

export function setPermissions(stageEntryId: string, permissions: Permissions): void {
  update(stageEntryId, {$merge: {permissions}}, {bumpVersion: false});
}

export function setTags(stageEntryId: string, tags: Array<SchemaTag>): void {
  update(stageEntryId, {$merge: {tags}}, {bumpVersion: false});
}

export async function saveFileData(
  stageEntryId: string,
  name: string,
  {success}: {success?: () => void} = {}
): Promise<void> {
  try {
    await StageEntryDAO.update(stageEntryId, {name});
    update(stageEntryId, {$merge: {name}}, {bumpVersion: false});
    if (success) {
      success();
    }
    // eslint-disable-next-line benchlint/always-throw-from-catch
  } catch (error) {
    notifications.notifyError(error);
  }
}

export function fetchInputDataForChildrenRuns(stageEntryId: string): void {
  LoadingScreen.show();
  StageEntryDAO.fetchInputDataForChildrenRuns(stageEntryId)
    .done(({affectedStageRuns}: {affectedStageRuns: Array<StageRun>}) => {
      StageRunActions.dispatchStageRunsFetchedAndFetchMissingSamples({
        stageRuns: affectedStageRuns,
      });
    })
    .always(() => {
      LoadingScreen.hide();
    });
}

export function print(
  entryId: string,
  {indexSlice, protocolCids}: {indexSlice?: {start: number; end: number}; protocolCids?: Array<string>}
): void {
  let p: EntryProtocol;
  const state = StageEntryStore.getEditorState(entryId);
  const {entryProtocols} = state.stageEntryDocument.entryVersion;
  if (protocolCids == null) {
    protocolCids = (() => {
      const result = [];
      for (p of Array.from(entryProtocols)) {
        result.push(p.cid);
      }
      return result;
    })();
  }

  function handlePrint(): void {
    const versionId = state.openVersionId;
    let args = `versionId=${versionId}`;
    const protocolIds = Array.from(protocolCids!).map((cid) => state.cidIdMap[cid]);
    args += `&protocols=${protocolIds.join(',')}`;
    if (indexSlice != null) {
      args += `&startIndex=${indexSlice.start}&endIndex=${indexSlice.end}`;
    }
    window.open(`${StageEntryUtil.getPrintURL(entryId)}?${args}#print`, '_blank');
  }

  // eslint-disable-next-line no-restricted-syntax
  const externalProtocols = _.filter(
    entryProtocols,
    (ep: EntryProtocol) => ep.externalFile != null && Array.from(protocolCids).includes(ep.cid)
  );
  if (externalProtocols.length > 0) {
    const message = (
      <div>
        <p>This entry contains imported protocols which cannot be printed:</p>
        <ul>
          {Array.from(externalProtocols).map((protocol) => (
            <li>{protocol.name}</li>
          ))}
        </ul>
        <p>Download and open these protocols directly to print them.</p>
      </div>
    );
    // It's only safe to use renderToStaticMarkup because formatSelection gets used in a non-React context.
    // See https://github.com/facebook/react/issues/3344
    new popup.Confirm({
      title: 'Print Entry',
      msg: ReactDOMServer.renderToStaticMarkup(message),
    })
      .choice('Print Entry', 'btn-af-primary', handlePrint)
      .choice('Cancel', 'btn-af-default')
      .show();
  } else {
    handlePrint();
  }
}

interface StageEntriesFetchCompletedAction {
  type: StageEntryActionType.WORKFLOW_STAGE_ENTRIES_FETCH_COMPLETED;
  stageEntries: Array<StageEntryDocument>;
}

export async function updateInputTableConfig(
  stageEntryId: string,
  {sortByType, sortByExperimentalConditionId, isSortReversed}: InputTableConfig
): Promise<void> {
  const response = await StageEntryDAO.updateInputTableConfig(stageEntryId, {
    sortByType,
    sortByExperimentalConditionId,
    isSortReversed,
  });

  const stageEntry = response!.stageEntry;

  getStore().dispatch({
    type: StageEntryActionType.WORKFLOW_STAGE_ENTRIES_FETCH_COMPLETED,
    stageEntries: [stageEntry],
  });

  notifications.success('Sorting order of the input samples table saved');
}

export type StageEntryAction = StageEntrySaveCompletedAction | StageEntriesFetchCompletedAction;
